// Function index:
//   runAuth() — drives the title screen's login/signup form. Toggles between
//     log-in and sign-up modes, supports the "Forgot password?" flow, and
//     resolves with the authenticated user. Caller is responsible for hiding
//     the form once the promise settles.

import { forgotPassword, login, signup, type AuthUser } from '../../api.js';

const $ = (id: string): HTMLElement => document.getElementById(id)!;

export function runAuth(): Promise<AuthUser> {
  const form = $('title-auth') as HTMLFormElement;
  const menu = $('title-menu');
  const submit = $('title-auth-submit') as HTMLButtonElement;
  const toggle = $('title-toggle-signup') as HTMLButtonElement;
  const forgot = $('title-forgot') as HTMLButtonElement;
  const displayRow = $('title-display-row');
  const error = $('title-auth-error');
  const info = $('title-auth-info');
  const footL = $('title-footer-left');

  form.hidden = false;
  menu.hidden = true;
  footL.textContent = 'Not signed in';

  const ac = new AbortController();
  const on = <K extends keyof HTMLElementEventMap>(el: HTMLElement, ev: K, fn: (e: HTMLElementEventMap[K]) => void): void =>
    el.addEventListener(ev, fn, { signal: ac.signal });

  let mode: 'login' | 'signup' = 'login';
  const setMode = (m: 'login' | 'signup'): void => {
    mode = m;
    submit.textContent = m === 'login' ? 'Log in' : 'Create account';
    toggle.textContent = m === 'login' ? 'Need an account? Sign up' : '← Back to log in';
    displayRow.hidden = m === 'login';
    forgot.hidden = m !== 'login';
    error.textContent = '';
    info.hidden = true;
  };
  setMode('login');

  return new Promise<AuthUser>((resolve) => {
    on(toggle, 'click', () => setMode(mode === 'login' ? 'signup' : 'login'));

    on(forgot, 'click', async () => {
      error.textContent = '';
      const current = String((form.elements.namedItem('email') as HTMLInputElement).value || '').trim();
      const email = current || (prompt('Email to send a reset link to:') || '').trim();
      if (!email) return;
      forgot.disabled = true;
      try {
        await forgotPassword(email);
        info.textContent = 'If that email is registered, a reset link has been sent.';
        info.hidden = false;
      } catch (err) {
        error.textContent = (err as Error).message;
      } finally {
        forgot.disabled = false;
      }
    });

    on(form, 'submit', async (e) => {
      e.preventDefault();
      error.textContent = '';
      info.hidden = true;
      submit.disabled = true;
      try {
        const fd = new FormData(form);
        const email = String(fd.get('email') ?? '');
        const password = String(fd.get('password') ?? '');
        const display_name = String(fd.get('display_name') ?? '');
        const result = mode === 'login'
          ? await login(email, password)
          : await signup(email, password, display_name || email.split('@')[0]!);
        ac.abort();
        form.hidden = true;
        resolve(result.user);
      } catch (err) {
        error.textContent = (err as Error).message;
        submit.disabled = false;
      }
    });
  });
}
